import {
    CheckCircleIcon,
    ExclamationTriangleIcon,
    PlusIcon,
    ShieldCheckIcon,
    UserGroupIcon,
    XCircleIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { format } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

/**
 * Admin team management.
 *
 * Only a super admin can add members or change access levels. The controls
 * are hidden for everyone else, but the real enforcement is server-side -
 * hiding a button is presentation, not security.
 */

const LEVELS = [
  {
    key: 'manager',
    label: 'Manager',
    blurb: 'View, approve and block. Cannot delete accounts or change pricing.',
    colour: 'bg-blue-100 text-blue-700',
  },
  {
    key: 'admin',
    label: 'Admin',
    blurb: 'Full platform management, including pricing and deletions.',
    colour: 'bg-orange-100 text-orange-700',
  },
  {
    key: 'super_admin',
    label: 'Super Admin',
    blurb: 'Everything, plus managing the admin team itself.',
    colour: 'bg-purple-100 text-purple-700',
  },
];

const levelMeta = (key) => LEVELS.find((l) => l.key === key) || LEVELS[0];

const emptyForm = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  password: '',
  level: 'manager',
};

const TeamManagement = () => {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [me, setMe] = useState(null);

  const token = () => localStorage.getItem('adminToken');
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  const isSuperAdmin = (() => {
    const roles = Array.isArray(me?.roles) && me.roles.length ? me.roles : [me?.role];
    return roles.includes('super_admin');
  })();

  useEffect(() => {
    const raw = localStorage.getItem('adminUser');
    if (raw) {
      try { setMe(JSON.parse(raw)); } catch (e) { /* ignore */ }
    }
    fetchTeam();
  }, []);

  const fetchTeam = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/team`,
        auth()
      );
      setTeam(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (error) {
      toast.error('Failed to load the admin team');
      setTeam([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    const { firstName, lastName, email, phone, password } = form;

    if (!firstName.trim() || !lastName.trim()) {
      toast.error('Please enter both first and last name');
      return;
    }
    if (!email.trim() || !phone.trim()) {
      toast.error('Email and phone are both required');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    try {
      setSaving(true);
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/team`,
        form,
        auth()
      );
      toast.success('Team member added');
      setForm(emptyForm);
      setShowForm(false);
      fetchTeam();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Could not add team member');
    } finally {
      setSaving(false);
    }
  };

  const handleLevelChange = async (member, level) => {
    if (level === member.role) return;

    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/admin/team/${member._id}/level`,
        { level },
        auth()
      );
      toast.success(`${member.firstName} is now ${levelMeta(level).label}`);
      fetchTeam();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Could not change access level');
    }
  };

  const handleToggleActive = async (member) => {
    const nextActive = !member.isActive;
    const action = nextActive ? 'reactivate' : 'suspend';

    if (!window.confirm(`Are you sure you want to ${action} ${member.firstName} ${member.lastName}?`)) {
      return;
    }

    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/admin/team/${member._id}/active`,
        { isActive: nextActive },
        auth()
      );
      toast.success(nextActive ? 'Team member reactivated' : 'Team member suspended');
      fetchTeam();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Could not update team member');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Team Management</h1>
          <p className="text-gray-600 mt-1">
            Admin accounts and what each person is allowed to do
          </p>
        </div>

        {isSuperAdmin && (
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            className="flex items-center px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-semibold"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Add Member
          </button>
        )}
      </div>

      {!isSuperAdmin && (
        <div className="flex items-start bg-blue-50 border border-blue-200 rounded-lg p-4">
          <ExclamationTriangleIcon className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-blue-800 ml-3">
            Only a super admin can add team members or change access levels.
          </p>
        </div>
      )}

      {/* What each level means */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {LEVELS.map((l) => (
          <div key={l.key} className="bg-white rounded-xl border border-gray-200 p-4">
            <span className={`inline-block px-2.5 py-1 rounded-md text-xs font-semibold ${l.colour}`}>
              {l.label}
            </span>
            <p className="text-sm text-gray-600 mt-2 leading-relaxed">{l.blurb}</p>
          </div>
        ))}
      </div>

      {/* Add form */}
      <AnimatePresence>
        {showForm && isSuperAdmin && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-white rounded-xl border border-gray-200 overflow-hidden"
          >
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Add a team member</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="text-sm text-gray-700">
                  First name
                  <input
                    type="text"
                    value={form.firstName}
                    onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </label>

                <label className="text-sm text-gray-700">
                  Last name
                  <input
                    type="text"
                    value={form.lastName}
                    onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </label>

                <label className="text-sm text-gray-700">
                  Email
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </label>

                <label className="text-sm text-gray-700">
                  Phone
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </label>

                <label className="text-sm text-gray-700">
                  Password
                  <input
                    type="text"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="At least 8 characters"
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-xs text-gray-500 mt-1 block">
                    Share this with them, then ask them to change it
                  </span>
                </label>

                <label className="text-sm text-gray-700">
                  Access level
                  <select
                    value={form.level}
                    onChange={(e) => setForm({ ...form, level: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {LEVELS.map((l) => (
                      <option key={l.key} value={l.key}>{l.label}</option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="flex items-center gap-3 mt-5">
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={saving}
                  className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-semibold disabled:opacity-60"
                >
                  {saving ? 'Adding...' : 'Add Member'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setForm(emptyForm); }}
                  className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-semibold"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Team list */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center">
          <UserGroupIcon className="w-5 h-5 text-gray-500 mr-2" />
          <h2 className="font-semibold text-gray-900">
            {team.length} {team.length === 1 ? 'member' : 'members'}
          </h2>
        </div>

        {team.length === 0 ? (
          <div className="py-12 text-center text-gray-500 text-sm">
            No admin accounts found.
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {team.map((member) => {
              const meta = levelMeta(member.role);
              const isMe = String(member._id) === String(me?._id || me?.id);

              return (
                <div key={member._id} className="px-6 py-4 flex items-center flex-wrap gap-4">
                  <div className="w-11 h-11 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-primary-600 font-bold">
                      {(member.firstName || '?')[0]?.toUpperCase()}
                    </span>
                  </div>

                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-gray-900">
                        {member.firstName} {member.lastName}
                      </p>
                      {isMe && (
                        <span className="text-xs text-gray-500">(you)</span>
                      )}
                      {!member.isActive && (
                        <span className="px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700">
                          Suspended
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{member.email}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {member.lastLogin
                        ? `Last signed in ${format(new Date(member.lastLogin), 'd MMM yyyy')}`
                        : 'Never signed in'}
                    </p>
                  </div>

                  <span className={`px-3 py-1 rounded-md text-xs font-semibold ${meta.colour}`}>
                    {meta.label}
                  </span>

                  {isSuperAdmin && !isMe && (
                    <div className="flex items-center gap-2">
                      <select
                        value={member.role}
                        onChange={(e) => handleLevelChange(member, e.target.value)}
                        className="px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        {LEVELS.map((l) => (
                          <option key={l.key} value={l.key}>{l.label}</option>
                        ))}
                      </select>

                      <button
                        type="button"
                        onClick={() => handleToggleActive(member)}
                        title={member.isActive ? 'Suspend' : 'Reactivate'}
                        className={`p-2 rounded-lg ${
                          member.isActive
                            ? 'text-red-600 hover:bg-red-50'
                            : 'text-green-600 hover:bg-green-50'
                        }`}
                      >
                        {member.isActive
                          ? <XCircleIcon className="w-5 h-5" />
                          : <CheckCircleIcon className="w-5 h-5" />}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex items-start bg-gray-50 border border-gray-200 rounded-lg p-4">
        <ShieldCheckIcon className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-gray-600 ml-3">
          Every member signs in with their own account, so platform changes can be
          traced back to a person. Suspending someone blocks their access immediately
          without affecting anyone else.
        </p>
      </div>
    </div>
  );
};

export default TeamManagement;
